import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ContentSnippet {
  id: string;
  content: string;
  description: string | null;
  snippet_type: string;
}

interface UseContentSnippetsResult {
  snippets: Record<string, string>;
  loading: boolean;
  error: string | null;
  getSnippet: (id: string, fallback?: string) => string;
  refreshSnippets: () => Promise<void>;
}

/**
 * Hook zum Laden und Abrufen von Content-Snippets aus der Datenbank.
 * 
 * @example
 * const { snippets, loading, getSnippet } = useContentSnippets();
 * 
 * // Verwendung:
 * <h1>{getSnippet('home_hero_h1', 'Default Titel')}</h1>
 */
export function useContentSnippets(): UseContentSnippetsResult {
  const [snippets, setSnippets] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSnippets = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("content_snippets")
        .select("id, content, description, snippet_type");

      if (fetchError) throw fetchError;

      // In Key-Value-Map konvertieren für schnellen Zugriff
      const snippetMap: Record<string, string> = {};
      data?.forEach((snippet: ContentSnippet) => {
        snippetMap[snippet.id] = snippet.content;
      });

      setSnippets(snippetMap);
    } catch (err: any) {
      console.error("Fehler beim Laden der Content-Snippets:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnippets();

    // Realtime-Updates abonnieren (optional, für Admin-Preview)
    const channel = supabase
      .channel("content_snippets_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "content_snippets",
        },
        () => {
          loadSnippets();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  /**
   * Sicherer Zugriff auf Snippet mit Fallback.
   * Wenn Snippet nicht existiert, wird der Fallback zurückgegeben.
   */
  const getSnippet = (id: string, fallback: string = ""): string => {
    return snippets[id] || fallback;
  };

  return {
    snippets,
    loading,
    error,
    getSnippet,
    refreshSnippets: loadSnippets,
  };
}
